import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Lock, MapPin, User, Settings, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { clientPortalService } from '../../services/clientPortal.service';
import { toast } from 'sonner';
import { AccountTab } from './profile/AccountTab';
import { ChangePasswordCard } from './profile/ChangePasswordCard';
import { LocationsTab } from './profile/LocationsTab';
import { ContactsTab } from './profile/ContactsTab';
import { PreferencesTab } from './profile/PreferencesTab';
import { DocumentsTab } from './profile/DocumentsTab';
import { DocumentViewerDialog } from './profile/DocumentViewerDialog';
import { LocationFormDialog } from './profile/LocationFormDialog';
import { getClientPermissions } from '../../lib/clientPermissions';

const EMPTY_LOCATION = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  delivery_instructions: '',
  receiving_hours: '',
  is_default: false,
};

const Profile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState(EMPTY_LOCATION);
  const [savingLocation, setSavingLocation] = useState(false);
  const [clientDocs, setClientDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);

  // Deep-link support: /client/profile?tab=documents opens the Documents tab
  // directly. This is what the "Review Document" button in the acknowledgment
  // email points at, and it also lets us link users to any specific tab from
  // other places in the app (dashboard alerts, notifications, etc.).
  const validTabs = ['account', 'security', 'locations', 'contacts', 'preferences', 'documents'];
  const requestedTab = searchParams.get('tab');
  const activeTab = validTabs.includes(requestedTab) ? requestedTab : 'account';
  const handleTabChange = (val) => {
    const next = new URLSearchParams(searchParams);
    if (val === 'account') next.delete('tab');
    else next.set('tab', val);
    setSearchParams(next, { replace: true });
  };

  const loadProfile = useCallback(async () => {
    try {
      const data = await clientPortalService.getProfile();
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const data = await clientPortalService.getMyDocuments();
      setClientDocs(data);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
    loadDocuments();
  }, [loadProfile, loadDocuments]);

  const handleAcknowledge = async (docId) => {
    setAcknowledging(true);
    try {
      await clientPortalService.acknowledgeDocument(docId);
      toast.success('Document acknowledged successfully');
      setViewingDoc(null);
      loadDocuments();
    } catch (error) {
      console.error('Error acknowledging document:', error);
      toast.error('Failed to acknowledge document');
    } finally {
      setAcknowledging(false);
    }
  };

  const openAddLocation = () => {
    setEditingLocation(null);
    setLocationForm(EMPTY_LOCATION);
    setShowLocationDialog(true);
  };

  const openEditLocation = (location) => {
    setEditingLocation(location);
    setLocationForm({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zip_code: location.zip_code || '',
      delivery_instructions: location.delivery_instructions || '',
      receiving_hours: location.receiving_hours || '',
      is_default: location.is_default || false,
    });
    setShowLocationDialog(true);
  };

  const handleSaveLocation = async () => {
    if (!locationForm.name || !locationForm.address || !locationForm.city || !locationForm.state || !locationForm.zip_code) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingLocation(true);
    try {
      const url = editingLocation
        ? `${process.env.REACT_APP_BACKEND_URL}/api/client-portal/locations/${editingLocation.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/client-portal/locations`;

      const response = await fetch(url, {
        method: editingLocation ? 'PUT' : 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(locationForm),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save location');
      }

      toast.success(editingLocation ? 'Location updated' : 'Location added');
      setShowLocationDialog(false);
      loadProfile();
    } catch (error) {
      toast.error(error.message || 'Failed to save location');
    } finally {
      setSavingLocation(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/locations/${locationId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to delete location');

      toast.success('Location deleted');
      loadProfile();
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  const handleUpdatePreference = async (key, newVal) => {
    try {
      await clientPortalService.updateNotificationPreferences({ [key]: newVal });
      setProfile(prev => ({
        ...prev,
        account: { ...prev.account, preferences: { ...prev.account.preferences, [key]: newVal } },
      }));
      toast.success('Preference updated');
    } catch {
      toast.error('Failed to update preference');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="profile-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Failed to load profile</p>
        <Button onClick={loadProfile} className="mt-4">Retry</Button>
      </div>
    );
  }

  const { account, locations } = profile;
  const docActionCount = clientDocs.filter(d => d.status === 'pending_acknowledgment' || d.is_expired).length;
  const canEditProfile = getClientPermissions().can_edit_profile;
  const canManageContacts = getClientPermissions().can_manage_users;

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="text-sm text-gray-600 mt-1">Manage your account settings</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList>
          <TabsTrigger value="account" data-testid="tab-account">
            <Building2 className="h-4 w-4 mr-2" />
            Account
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Lock className="h-4 w-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">
            <MapPin className="h-4 w-4 mr-2" />
            Locations
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">
            <User className="h-4 w-4 mr-2" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="preferences" data-testid="tab-preferences">
            <Settings className="h-4 w-4 mr-2" />
            Preferences
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <FileText className="h-4 w-4 mr-2" />
            Documents
            {docActionCount > 0 && (
              <span className="ml-1.5 bg-amber-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {docActionCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="account">
          <AccountTab account={account} />
        </TabsContent>

        <TabsContent value="security">
          <ChangePasswordCard />
        </TabsContent>

        <TabsContent value="locations">
          <LocationsTab
            locations={locations}
            onAdd={openAddLocation}
            onEdit={openEditLocation}
            onDelete={handleDeleteLocation}
            canEdit={canEditProfile}
          />
        </TabsContent>

        <TabsContent value="contacts">
          <ContactsTab account={account} canManageContacts={canManageContacts} />
        </TabsContent>

        <TabsContent value="preferences">
          <PreferencesTab account={account} onUpdatePreference={handleUpdatePreference} canEdit={canEditProfile} />
        </TabsContent>

        <TabsContent value="documents" data-testid="documents-tab-content">
          <DocumentsTab clientDocs={clientDocs} loadingDocs={loadingDocs} onView={setViewingDoc} />
        </TabsContent>
      </Tabs>

      <DocumentViewerDialog
        viewingDoc={viewingDoc}
        onClose={() => setViewingDoc(null)}
        onAcknowledge={handleAcknowledge}
        acknowledging={acknowledging}
        canAcknowledge={canEditProfile}
      />

      <LocationFormDialog
        open={showLocationDialog}
        onOpenChange={setShowLocationDialog}
        editingLocation={editingLocation}
        locationForm={locationForm}
        setLocationForm={setLocationForm}
        onSave={handleSaveLocation}
        saving={savingLocation}
      />
    </div>
  );
};

export default Profile;
